// Prijsafwijkingen t.o.v. de standaardprijs (SPEC 27okt 2). Afwijking = elk verschil, hoger of
// lager, tussen de eenheidsprijs van een post en de standaardprijs uit de prijslijst(versie) van de
// onderaannemer. Geen drempel in deze versie (open punt 10.1). Pure functies, gedeeld door de server
// (validatie, mail, blok) en de browser (live blok en redenvraag tijdens het bewerken).
import { round2 } from './schijven.js';
import { lineSubtotaal } from './matching.js';
import type { GevelConfig, GevelResultaat } from './gevel.js';
import type { MatchedLine, MeerwerkLine } from './types.js';

/** Minimale lengte van de reden bij een afwijkende prijs (27okt 2). */
export const PRIJSAFWIJKING_REDEN_MIN = 5;

export interface Prijsafwijking {
  /** 'werkpost:<lijn-id>' of 'meerwerk:<id>' — stabiel over bewerkingen heen. */
  regelSleutel: string;
  soort: 'werkpost' | 'meerwerk';
  lijnId: string;
  post: string;
  eenheid: string;
  hoeveelheid: number;
  standaard: number;
  toegepast: number;
  verschilPerEenheid: number;
  /** In procent van de standaardprijs; null bij een standaardprijs van 0. */
  verschilPct: number | null;
  /** Impact op het totaal = verschil per eenheid × hoeveelheid. Positief = duurder voor Recotex. */
  impact: number;
  reden: string;
}

export interface StandaardPrijsBron {
  /** Prijs van een prijslijstpost in de prijslijstversie van de calculatie; null = onbekend of NVT. */
  itemPrijs: (plItemId: string) => number | null;
  gevel?: GevelConfig | null;
  /** Resultaat van de gevelladder: posten die de ladder prijst, hebben geen eigen eenheidsprijs. */
  gevelResultaat?: GevelResultaat | null;
}

/** Standaardprijs van een werkpost, of null als de post er geen heeft (ladder, schijven, geen prijslijstpost). */
export function standaardPrijsWerkpost(l: MatchedLine, bron: StandaardPrijsBron): number | null {
  if (l.schijven) return null;
  if (bron.gevelResultaat?.vervangenLijnIds.includes(l.id)) return null;
  if (l.gevelAccessoire) {
    const acc = bron.gevel?.accessoires?.[l.gevelAccessoire];
    if (acc) return acc.prijs;
  }
  const plItemId = l.subItems[0]?.plItemId;
  return plItemId ? bron.itemPrijs(plItemId) : null;
}

function toegepastePrijs(l: MatchedLine): number | null {
  if (l.prijs != null) return l.prijs;
  if (!l.hoeveelheid) return null;
  return round2(lineSubtotaal({ ...l, overlap: undefined }) / l.hoeveelheid);
}

function maak(
  soort: Prijsafwijking['soort'],
  lijnId: string,
  post: string,
  eenheid: string,
  hoeveelheid: number,
  standaard: number,
  toegepast: number,
  reden: string | undefined,
): Prijsafwijking {
  const verschil = round2(toegepast - standaard);
  return {
    regelSleutel: `${soort}:${lijnId}`,
    soort,
    lijnId,
    post,
    eenheid,
    hoeveelheid,
    standaard,
    toegepast,
    verschilPerEenheid: verschil,
    verschilPct: standaard > 0 ? (verschil / standaard) * 100 : null,
    impact: round2(verschil * (hoeveelheid || 0)),
    reden: (reden ?? '').trim(),
  };
}

const VERSCHILT = (a: number, b: number) => Math.abs(a - b) >= 0.005;

/** Alle posten waarvan de eenheidsprijs afwijkt van de standaardprijs. */
export function vindPrijsafwijkingen(
  matchedLines: MatchedLine[],
  meerwerkLines: MeerwerkLine[],
  bron: StandaardPrijsBron,
): Prijsafwijking[] {
  const out: Prijsafwijking[] = [];
  for (const l of matchedLines) {
    // Een post die door de overlapcontrole niet betaald wordt, heeft geen prijsimpact.
    if (l.overlap && l.overlap.status !== 'bevestigd') continue;
    const standaard = standaardPrijsWerkpost(l, bron);
    const toegepast = toegepastePrijs(l);
    if (standaard == null || toegepast == null || !VERSCHILT(standaard, toegepast)) continue;
    out.push(maak('werkpost', l.id, l.naamArbeid, l.eenheid, l.hoeveelheid, standaard, toegepast, l.prijsAfwijkingReden));
  }
  for (const m of meerwerkLines) {
    if (bron.gevelResultaat?.vervangenMeerwerkIds.includes(m.id)) continue;
    const standaard = m.plItemId ? bron.itemPrijs(m.plItemId) : null;
    if (standaard == null || !VERSCHILT(standaard, m.prijs)) continue;
    out.push(maak('meerwerk', m.id, m.naamArbeid || m.pdfNaam, m.eenheid, m.hoeveelheid, standaard, m.prijs, m.prijsAfwijkingReden));
  }
  return out;
}

export function totaleImpact(afwijkingen: Prijsafwijking[]): number {
  return round2(afwijkingen.reduce((s, a) => s + a.impact, 0));
}

/** Twee afwijkingen zijn "dezelfde" melding als post, standaard- en toegepaste prijs gelijk zijn. */
export function zelfdeAfwijking(
  a: Pick<Prijsafwijking, 'regelSleutel' | 'standaard' | 'toegepast'>,
  b: Pick<Prijsafwijking, 'regelSleutel' | 'standaard' | 'toegepast'>,
): boolean {
  return a.regelSleutel === b.regelSleutel && !VERSCHILT(a.standaard, b.standaard) && !VERSCHILT(a.toegepast, b.toegepast);
}

export function redenGeldig(reden: string | undefined | null): boolean {
  return (reden ?? '').trim().length >= PRIJSAFWIJKING_REDEN_MIN;
}
